import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAccount, useSignMessage, useConnect } from "wagmi";
import { Oval } from "react-loader-spinner";

import Container from "../components/common/Container";
import { API } from "../api";
import WalletDisplay from "../components/common/WalletDisplay";

/**
 * TelegramVerify Page
 * 
 * Handles wallet verification for Telegram account linking.
 * Users open this page from QR code or link to connect their wallet.
 */
const TelegramVerify = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { address, isConnected } = useAccount();
  const { connectors, connect, isPending: isConnecting } = useConnect();
  const { signMessageAsync } = useSignMessage();

  const [status, setStatus] = useState("loading"); // loading, ready, signing, success, error
  const [verificationData, setVerificationData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Load verification details
  useEffect(() => {
    const loadVerification = async () => {
      try {
        const response = await API.get(`/telegram/verify/${token}`);
        setVerificationData(response.data?.data || response.data);
        setStatus("ready");
      } catch (err) {
        setStatus("error");
        setErrorMessage(
          err.response?.data?.message || 
          "Verification link expired or invalid. Please request a new link from Telegram."
        );
      }
    };

    if (token) {
      loadVerification();
    }
  }, [token]);

  // Handle signing when wallet is connected
  const handleSign = async () => {
    if (!isConnected || !address || !verificationData) return;

    try {
      setStatus("signing");
      
      const signature = await signMessageAsync({
        message: verificationData.challenge,
      });

      // Submit verification
      await API.post("/telegram/verify/link", {
        token,
        signature,
        walletAddress: address,
      });

      setStatus("success");
    } catch (err) {
      setStatus("error");
      if (err.name === "UserRejectedRequestError" || err.message?.includes("rejected")) {
        setErrorMessage("Signature request was rejected. Please try again.");
      } else {
        setErrorMessage(
          err.response?.data?.message || 
          err.message || 
          "Verification failed. Please try again."
        );
      }
    }
  };

  return (
    <Container>
      <div className="min-h-screen flex items-center justify-center py-12">
        <div className="max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Link Your Wallet</h1>
            <p className="text-gray-600 mt-2">
              Connect your wallet to Telegram for NFT-gated access
            </p>
          </div>

          {/* Loading */}
          {status === "loading" && (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <Oval
                height={48}
                width={48}
                color="#6366f1"
                secondaryColor="#a5b4fc"
                strokeWidth={4}
                strokeWidthSecondary={4}
                wrapperClass="justify-center mb-4"
              />
              <p className="text-gray-600">Loading verification details...</p>
            </div>
          )}

          {/* Ready - Show connect/sign options */}
          {status === "ready" && (
            <div className="bg-white rounded-2xl shadow-lg p-8">
              {!isConnected ? (
                <>
                  <p className="text-gray-600 mb-6 text-center">
                    Connect your wallet to verify ownership and link it to your Telegram account.
                  </p>
                  
                  <div className="space-y-3">
                    {connectors.map((connector) => (
                      <button
                        key={connector.uid}
                        onClick={() => connect({ connector })}
                        disabled={isConnecting}
                        className="w-full bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-xl p-4 flex items-center gap-3 transition-colors disabled:opacity-50"
                      >
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                          {connector.icon ? (
                            <img src={connector.icon} alt="" className="w-6 h-6" />
                          ) : (
                            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                          )}
                        </div>
                        <span className="font-medium text-gray-900">{connector.name}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
                    <div className="flex items-center gap-2 text-green-700 mb-1">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="font-medium">Wallet Connected</span>
                    </div>
                    <p className="text-green-600 font-mono text-sm">
                      <WalletDisplay address={address} />
                    </p>
                  </div>

                  <p className="text-gray-600 mb-6">
                    Sign the verification message to link this wallet to your Telegram account.
                    This won't cost any gas.
                  </p>

                  <button
                    onClick={handleSign}
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl p-4 font-medium hover:opacity-90 transition-opacity"
                  >
                    Sign Message to Verify
                  </button>
                </>
              )}
            </div>
          )}

          {/* Signing */}
          {status === "signing" && (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <Oval
                height={48}
                width={48}
                color="#6366f1"
                secondaryColor="#a5b4fc"
                strokeWidth={4}
                strokeWidthSecondary={4}
                wrapperClass="justify-center mb-4"
              />
              <p className="text-gray-600">
                Please sign the message in your wallet...
              </p>
            </div>
          )}

          {/* Success */}
          {status === "success" && (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Verification Complete!</h2>
              <p className="text-gray-600 mb-6">
                Your wallet is now linked to your Telegram account. 
                You can close this page and return to Telegram.
              </p>
              <p className="text-sm text-gray-500">
                Tap "Check Status" in the Telegram app to see your communities.
              </p>
            </div>
          )}

          {/* Error */}
          {status === "error" && (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Verification Failed</h2>
              <p className="text-gray-600 mb-6">{errorMessage}</p>
              <button
                onClick={() => {
                  setStatus("ready");
                  setErrorMessage("");
                }}
                className="w-full bg-indigo-500 text-white rounded-xl p-4 font-medium hover:bg-indigo-600 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Footer */}
          <p className="text-center text-sm text-gray-500 mt-6">
            Powered by FunDeSci
          </p>
        </div>
      </div>
    </Container>
  );
};

export default TelegramVerify;
